/**
 * Notes service for the ms-agents backend (Flex Comms module).
 *
 * Each function takes the ms-agents verb (imported from `commsApi.js`) as its
 * first argument. Return-shape note: `get`/`patch` return `response.data`;
 * `post`/`remove` return `{ data, status }`.
 */
import { buildQuery } from "./_query";

/**
 * List notes attached to a room.
 *
 * @param {Function} get - ms-agents GET verb.
 * @param {string} roomId - Room id.
 * @param {boolean} [pinnedOnly=false] - Only return pinned notes.
 * @returns {Promise<Object>} Notes payload.
 */
export const listNotes = async (get, roomId, pinnedOnly = false) => {
  try {
    return await get(`/api/v1/rooms/${roomId}/notes${buildQuery({ pinned_only: pinnedOnly })}`);
  } catch (err) {
    const message = err?.response?.data?.message || "Error fetching notes";
    console.error(message);
    throw new Error(message);
  }
};

/**
 * Create a note on a room.
 *
 * @param {Function} post - ms-agents POST verb.
 * @param {string} roomId - Room id.
 * @param {Object} payload - `{ content, note_type, pinned, linked_message_id }`.
 *   note_type: `GENERAL` | `MEDICAL` | `PREFERENCE` | `FROM_CHAT`.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const createNote = async (post, roomId, payload) => {
  try {
    return await post(`/api/v1/rooms/${roomId}/notes`, payload);
  } catch (err) {
    const message = err?.response?.data?.message || "Error creating note";
    console.error(message);
    throw new Error(message);
  }
};

/**
 * Update a note.
 *
 * @param {Function} patch - ms-agents PATCH verb.
 * @param {string} roomId - Room id.
 * @param {string} noteId - Note id.
 * @param {Object} payload - Partial note fields to update.
 * @returns {Promise<Object>} Updated note.
 */
export const updateNote = async (patch, roomId, noteId, payload) => {
  try {
    return await patch(`/api/v1/rooms/${roomId}/notes/${noteId}`, payload);
  } catch (err) {
    const message = err?.response?.data?.message || "Error updating note";
    console.error(message);
    throw new Error(message);
  }
};

/**
 * Delete a note.
 *
 * @param {Function} remove - ms-agents DELETE verb.
 * @param {string} roomId - Room id.
 * @param {string} noteId - Note id.
 * @returns {Promise<{ data: Object, status: number }>}
 */
export const deleteNote = async (remove, roomId, noteId) => {
  try {
    return await remove(`/api/v1/rooms/${roomId}/notes/${noteId}`);
  } catch (err) {
    const message = err?.response?.data?.message || "Error deleting note";
    console.error(message);
    throw new Error(message);
  }
};
